import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useCreateDiagram, useDiagrams, useProjects } from '../api/queries'
import type { DiagramSummary, Paged, Project } from '../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDateTime } from '../lib/format'
import { projectOptionLabel } from '../lib/projectLabel'
import { NewProjectForm } from './ProjectsPage'

/**
 * Diagrammer (beslutning 150) - kun Projektleder og opefter. En ny tegning starter med at vælge sagen; findes
 * sagen ikke endnu, oprettes den her med den almindelige formular.
 */
export function DiagramsPage() {
  const [params] = useSearchParams()
  const preset = Number(params.get('projectId')) || null
  const { data, isLoading, error } = useDiagrams()
  const [creating, setCreating] = useState(preset !== null)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <p className="small muted" style={{ margin: 0, maxWidth: 640 }}>
          Tegn enstregsdiagrammer med træk-og-slip. Tegningen gemmes på sagen og kan rettes igen, og "Gem som PDF i
          sagen" lægger den i sagens Dokumenter-mappe. Alt, der oprettes og rettes, står i sagens aktivitetslog.
        </p>
        {!creating && (
          <button className="btn btn--primary" onClick={() => setCreating(true)}>+ Ny tegning</button>
        )}
      </div>

      {creating && <NewDiagram presetProjectId={preset} onCancel={() => setCreating(false)} />}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && data.length === 0 && !creating && <EmptyState>Der er ingen diagrammer endnu.</EmptyState>}
      {data && data.length > 0 && <DiagramList diagrams={data} showProject />}
    </div>
  )
}

/** Listen over diagrammer - også brugt på sagen. */
export function DiagramList({ diagrams, showProject }: { diagrams: DiagramSummary[]; showProject?: boolean }) {
  return (
    <Card>
      <div className="stack" style={{ gap: 2 }}>
        {diagrams.map((d) => (
          <Link key={d.id} to={`/diagrams/${d.id}`} className="device-row row" style={{ gap: 10, color: 'inherit', textDecoration: 'none' }}>
            <span style={{ fontSize: 22 }} aria-hidden>📐</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>{d.title}</strong>
              {showProject && (
                <div className="tiny muted">
                  {d.projectOrderNumber ? `Ordre ${d.projectOrderNumber} · ` : ''}
                  {d.projectName}
                </div>
              )}
              <div className="tiny muted">
                Rettet {formatDateTime(d.updatedAt)}
                {d.updatedBy ? ` af ${d.updatedBy}` : ''}
                {d.pdfSavedAt ? ' · 📄 PDF i sagen' : ''}
                {d.uploadError ? ' · ⚠️ PDF kom ikke i sagens mappe' : ''}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Card>
  )
}

function NewDiagram({ presetProjectId, onCancel }: { presetProjectId: number | null; onCancel: () => void }) {
  const navigate = useNavigate()
  const create = useCreateDiagram()
  const { data: projects } = useProjects({ pageSize: 200, done: false }) as { data?: Paged<Project> }
  const [projectId, setProjectId] = useState<string>(presetProjectId ? String(presetProjectId) : '')
  const [title, setTitle] = useState('Batterianlæg')
  const [newProject, setNewProject] = useState(false)
  const [createdName, setCreatedName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    if (!projectId) return setError('Vælg sagen, diagrammet hører til.')
    if (!title.trim()) return setError('Giv diagrammet et navn.')
    try {
      const created = await create.mutateAsync({ projectId: Number(projectId), title: title.trim() })
      navigate(`/diagrams/${created.summary.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Diagrammet kunne ikke oprettes.')
    }
  }

  if (newProject) {
    return (
      <div className="stack" style={{ gap: 8 }}>
        <div className="row row--between">
          <strong>Opret sagen først</strong>
          <button className="btn btn--sm" onClick={() => setNewProject(false)}>Tilbage til diagrammet</button>
        </div>
        <NewProjectForm
          onDone={() => setNewProject(false)}
          onCreated={(created) => {
            setProjectId(String(created.project.id))
            setCreatedName(created.project.name)
            setNewProject(false)
          }}
        />
      </div>
    )
  }

  const known = projects?.items.some((p) => String(p.id) === projectId)

  return (
    <Card title="Ny tegning">
      <div className="stack" style={{ gap: 10 }}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Sag *</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Vælg sag</option>
              {projectId && !known && <option value={projectId}>{createdName ?? `Sag ${projectId}`}</option>}
              {projects?.items.map((p) => (
                <option key={p.id} value={p.id}>{projectOptionLabel(p)}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Navn på diagrammet *</span>
            <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          </label>
        </div>
        <button type="button" className="link-button small" style={{ alignSelf: 'flex-start' }} onClick={() => setNewProject(true)}>
          Findes sagen ikke? Opret den her
        </button>
        {createdName && <div className="tiny">✓ Sagen "{createdName}" er oprettet og valgt.</div>}
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={create.isPending} onClick={() => void submit()}>
            {create.isPending ? 'Opretter ...' : 'Start tegningen'}
          </button>
          <button className="btn" onClick={onCancel}>Annullér</button>
        </div>
      </div>
    </Card>
  )
}
